import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { ApiError, api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface FieldDef {
  key: 'schemaPrompt' | 'modeIngestPrompt' | 'modeHealthPrompt' | 'chatInjectPrompt';
  label: string;
  hint: string;
}

const FIELDS: FieldDef[] = [
  {
    key: 'schemaPrompt',
    label: '스키마 (위키 운영 규칙)',
    hint: 'Karpathy 의 schema — 모든 모드의 LLM 호출에 prefix 로 주입. takeaway/[[wikilinks]]/카테고리 규칙 정의.',
  },
  {
    key: 'modeIngestPrompt',
    label: '자료 넣기 모드',
    hint: 'wiki.ingest 워커가 새 자료를 정리할 때 추가로 주입.',
  },
  {
    key: 'modeHealthPrompt',
    label: '건강검진 모드',
    hint: 'wiki.health-check 워커가 모순/고아 점검 시 주입.',
  },
  {
    key: 'chatInjectPrompt',
    label: '챗 주입 (wiki-explorer 활성 시)',
    hint: '사용자 챗에서 wiki-explorer 스킬이 활성화된 경우 system 메시지로 자동 주입. 사용자 질문 답변 시 사용되는 유일한 위키 보조 프롬프트.',
  },
];

export function AdminWikiConfig() {
  const queryClient = useQueryClient();
  const configQuery = useQuery({
    queryKey: ['admin', 'wiki', 'config'],
    queryFn: api.adminWikiConfig,
  });
  const endpointsQuery = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: api.adminLlmList,
  });

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [concurrency, setConcurrency] = useState<number>(2);
  const [charCap, setCharCap] = useState<number>(50000);
  const [iterLimit, setIterLimit] = useState<number>(15);
  const [maxChunks, setMaxChunks] = useState<number>(50);
  const [chunkMaxChars, setChunkMaxChars] = useState<number>(6000);
  const [chunkOverlap, setChunkOverlap] = useState<number>(200);
  const [visionEndpointId, setVisionEndpointId] = useState<string>('');
  const [ingestEndpointId, setIngestEndpointId] = useState<string>('');
  const [healthEndpointId, setHealthEndpointId] = useState<string>('');
  const [embeddingEndpointId, setEmbeddingEndpointId] = useState<string>('');
  const [invalidateMismatched, setInvalidateMismatched] = useState<boolean>(false);
  const [rerankerEnabled, setRerankerEnabled] = useState<boolean>(false);
  const [rerankerEndpointId, setRerankerEndpointId] = useState<string>('');
  const [rerankerTopK, setRerankerTopK] = useState<number>(8);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const embeddingsByEndpointQuery = useQuery({
    queryKey: ['admin', 'wiki', 'embeddings-by-endpoint'],
    queryFn: api.adminWikiEmbeddingsByEndpoint,
    staleTime: 30_000,
  });

  // 데이터가 로드되면 draft 초기값 채움 (한 번만).
  useEffect(() => {
    const cfg = configQuery.data;
    if (!cfg) return;
    setDraft({
      schemaPrompt: cfg.schemaPrompt,
      modeIngestPrompt: cfg.modeIngestPrompt,
      modeHealthPrompt: cfg.modeHealthPrompt,
      chatInjectPrompt: cfg.chatInjectPrompt,
    });
    setConcurrency(cfg.ingestConcurrency);
    setCharCap(cfg.ingestMarkdownCharCap);
    setIterLimit(cfg.ingestToolIterationLimit);
    setMaxChunks(cfg.ingestMaxChunks);
    setChunkMaxChars(cfg.ingestChunkMaxChars);
    setChunkOverlap(cfg.ingestChunkOverlap);
    setVisionEndpointId(cfg.visionLlmEndpointId ?? '');
    setIngestEndpointId(cfg.ingestLlmEndpointId ?? '');
    setHealthEndpointId(cfg.healthLlmEndpointId ?? '');
    setEmbeddingEndpointId(cfg.embeddingLlmEndpointId ?? '');
    setRerankerEnabled(!!cfg.rerankerLlmEndpointId);
    setRerankerEndpointId(cfg.rerankerLlmEndpointId ?? '');
    setRerankerTopK(cfg.rerankerTopK);
  }, [configQuery.data]);

  const updateMutation = useMutation({
    mutationFn: api.adminWikiConfigUpdate,
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'wiki', 'config'], data);
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'embeddings-by-endpoint'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'graph-stats'] });
      setSavedMsg(invalidateMismatched ? '저장됨 — 임베딩 invalidate + backfill 큐잉됨' : '저장됨');
      setInvalidateMismatched(false);
      setTimeout(() => setSavedMsg(null), 3500);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const resetMutation = useMutation({
    mutationFn: api.adminWikiConfigResetPrompts,
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'wiki', 'config'], data);
      setDraft({
        schemaPrompt: data.schemaPrompt,
        modeIngestPrompt: data.modeIngestPrompt,
        modeHealthPrompt: data.modeHealthPrompt,
        chatInjectPrompt: data.chatInjectPrompt,
      });
      setSavedMsg('기본값으로 복원됨');
      setTimeout(() => setSavedMsg(null), 3000);
    },
  });

  return (
    <PageShell
      header={
        <PageHeader
          title="위키 시스템 프롬프트"
          description="위키 운영 규칙(Karpathy schema) 과 모드별 보조 지시. 변경 시 다음 ingest/query 부터 적용."
        />
      }
    >
      {configQuery.isLoading ? (
        <p className="text-sm text-foreground-muted">로딩 중…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-destructive">
          설정을 불러오지 못했습니다: {(configQuery.error as Error).message}
        </p>
      ) : (
        <div className="space-y-6">
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
            ⚠ <strong>위키 자료 정리(ingest)는 tool calling 을 적극 사용</strong>합니다. vLLM
            gemma-4 등 일부 모델은 큰 자료에서 instruction 을 흘려보내고 도구 호출 없이 텍스트로만
            답변할 수 있습니다. 그런 경우 자료가 done 상태인데 페이지가 0개 생기는 silent fail 이
            발생합니다.
            <br />
            <br />
            조치:
            <ul className="ml-4 mt-1 list-disc">
              <li>
                <strong>자료 글자 상한 (아래)</strong> 을 작게 (10000~15000) 유지 — 32k 컨텍스트
                모델에 안전.
              </li>
              <li>
                가능하면 <strong>Claude / GPT-4 / Gemini</strong> endpoint 를 등록해 아래의
                <strong> "자료 정리 전용 LLM endpoint"</strong> 에 지정.
              </li>
            </ul>
          </div>

          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          <section className="space-y-6">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="mb-1 block text-sm font-medium" htmlFor={f.key}>
                  {f.label}
                </label>
                <p className="mb-2 text-xs text-foreground-muted">{f.hint}</p>
                <textarea
                  id={f.key}
                  rows={f.key === 'schemaPrompt' ? 14 : 8}
                  value={draft[f.key] ?? ''}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.currentTarget.value })}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono leading-6 focus:border-accent focus:outline-none"
                />
              </div>
            ))}
          </section>

          <section className="rounded-md border border-border bg-surface p-4">
            <label className="mb-1 block text-sm font-medium" htmlFor="cfg-vision-endpoint">
              Vision LLM endpoint (이미지 캡션용)
            </label>
            <p className="mb-2 text-xs text-foreground-muted">
              위키 자료(PDF) 의 추출 이미지에 한국어 캡션을 다는 vision-capable endpoint. 미설정이면
              caption 단계 skip — 이미지는 페이지 본문(surroundingText) 만으로 LLM 이 추론. endpoint
              관리 화면에서 "Vision 지원" 을 켠 항목만 보입니다.
            </p>
            <select
              id="cfg-vision-endpoint"
              value={visionEndpointId}
              onChange={(e) => setVisionEndpointId(e.currentTarget.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
            >
              <option value="">(미설정 — caption 단계 skip)</option>
              {(endpointsQuery.data?.endpoints ?? [])
                .filter((e) => e.supportsVision)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.displayName ?? e.name} ({e.model})
                  </option>
                ))}
            </select>
            {endpointsQuery.data &&
            endpointsQuery.data.endpoints.filter((e) => e.supportsVision).length === 0 ? (
              <p className="mt-2 text-xs text-amber-600">
                Vision 지원 endpoint 가 없습니다. /admin/llm 에서 endpoint 의 "Vision 지원" 을
                켜세요.
              </p>
            ) : null}
          </section>

          <section className="rounded-md border border-border bg-surface p-4 space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cfg-ingest-endpoint">
                자료 정리 전용 LLM endpoint
              </label>
              <p className="mb-2 text-xs text-foreground-muted">
                wiki ingest 가 사용할 LLM. 미설정 시 시스템 기본 LLM 사용. tool calling 안정성 위해
                Claude / GPT-4 / Gemini 등 큰 모델 권장 (gemma 등 작은 모델은 instruction 손실
                가능).
              </p>
              <select
                id="cfg-ingest-endpoint"
                value={ingestEndpointId}
                onChange={(e) => setIngestEndpointId(e.currentTarget.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
              >
                <option value="">(미설정 — 시스템 기본 LLM 사용)</option>
                {(endpointsQuery.data?.endpoints ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.displayName ?? e.name} ({e.model})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cfg-health-endpoint">
                건강검진 전용 LLM endpoint
              </label>
              <p className="mb-2 text-xs text-foreground-muted">
                wiki health-check 가 사용할 LLM. 미설정 시 시스템 기본 LLM 사용. ingest 와 동일
                endpoint 를 써도 됩니다.
              </p>
              <select
                id="cfg-health-endpoint"
                value={healthEndpointId}
                onChange={(e) => setHealthEndpointId(e.currentTarget.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
              >
                <option value="">(미설정 — 시스템 기본 LLM 사용)</option>
                {(endpointsQuery.data?.endpoints ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.displayName ?? e.name} ({e.model})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cfg-embedding-endpoint">
                임베딩 endpoint (페이지 의미 검색)
              </label>
              <p className="mb-2 text-xs text-foreground-muted">
                wiki 페이지 임베딩 + hybrid search 에 사용. supportsEmbedding=true 인 endpoint 만
                보입니다 (BGE-M3, OpenAI text-embedding 등). 지정하면 부팅 시 모든 페이지 임베딩
                backfill 자동 큐잉.
              </p>
              <select
                id="cfg-embedding-endpoint"
                value={embeddingEndpointId}
                onChange={(e) => setEmbeddingEndpointId(e.currentTarget.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
              >
                <option value="">(미설정 — 의미 검색 비활성, tsvector 만)</option>
                {(endpointsQuery.data?.endpoints ?? [])
                  .filter((e) => e.supportsEmbedding)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.displayName ?? e.name} ({e.model}, dim {e.embeddingDim ?? '?'})
                    </option>
                  ))}
              </select>
              {endpointsQuery.data &&
              endpointsQuery.data.endpoints.filter((e) => e.supportsEmbedding).length === 0 ? (
                <p className="mt-2 text-xs text-amber-600">
                  Embedding 지원 endpoint 가 없습니다. /admin/llm/endpoints 에서 endpoint 의
                  "Embedding 지원" 을 켜세요 (예: BGE-M3, Qwen3-Embedding).
                </p>
              ) : null}
              {(() => {
                const rows = embeddingsByEndpointQuery.data?.rows ?? [];
                const newId = embeddingEndpointId || null;
                const mismatched = rows.filter((r) => r.endpointId !== newId);
                const mismatchedCount = mismatched.reduce((acc, r) => acc + r.count, 0);
                if (mismatchedCount === 0) return null;
                return (
                  <div className="mt-3 rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
                    <div className="font-medium text-amber-700 dark:text-amber-300">
                      ⚠️ 기존 임베딩 {mismatchedCount} 개가 다른 endpoint 로 만들어져 있습니다
                    </div>
                    <ul className="mt-1.5 ml-3 list-disc space-y-0.5 text-foreground-muted">
                      {mismatched.map((r) => (
                        <li key={r.endpointId ?? '_null'}>
                          {r.endpointName ?? '(삭제된 endpoint)'} · {r.endpointModel ?? '?'} · dim{' '}
                          {r.embeddingDim ?? '?'} — <strong>{r.count}</strong> 개
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-foreground-muted">
                      모델이 다르면 vector space 가 달라 의미 검색이 부정확해집니다. 저장 시 함께
                      삭제하고 새 endpoint 로 재계산할 수 있어요.
                    </p>
                    <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-amber-700 dark:text-amber-300">
                      <input
                        type="checkbox"
                        checked={invalidateMismatched}
                        onChange={(e) => setInvalidateMismatched(e.currentTarget.checked)}
                        className="size-3.5"
                      />
                      <span>저장 시 영향 받는 임베딩 모두 삭제 + backfill 큐잉</span>
                    </label>
                  </div>
                );
              })()}
            </div>
          </section>

          <section className="rounded-md border border-border bg-surface p-4 space-y-4">
            <div>
              <p className="mb-1 block text-sm font-medium">검색 리랭커 (결과 재정렬)</p>
              <p className="mb-2 text-xs text-foreground-muted">
                hybrid 검색(RRF) 상위 결과를 chat 모델로 다시 채점해 "더 맞는 페이지"를 위로
                올립니다. 체크하지 않거나 endpoint 를 고르지 않으면 RRF 결과를 그대로 쓰며 오류가
                나지 않습니다 (리랭크 skip). 리랭커는 <strong>chat 모델 endpoint</strong> 가
                필요합니다 (임베딩 endpoint 와 별개).
              </p>
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={rerankerEnabled}
                  onChange={(e) => setRerankerEnabled(e.currentTarget.checked)}
                  className="size-4"
                />
                <span>리랭커 사용 (검색 결과를 LLM 으로 재정렬)</span>
              </label>
            </div>
            {rerankerEnabled ? (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium" htmlFor="cfg-reranker-endpoint">
                    리랭커 LLM (활성 LLM 중 하나)
                  </label>
                  <p className="mb-2 text-xs text-foreground-muted">
                    검색 결과 재정렬에 쓸 chat 모델. 활성 LLM 중 하나만 선택합니다 (임베딩 전용
                    endpoint 제외). 미선택이면 리랭크 skip.
                  </p>
                  <select
                    id="cfg-reranker-endpoint"
                    value={rerankerEndpointId}
                    onChange={(e) => setRerankerEndpointId(e.currentTarget.value)}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
                  >
                    <option value="">(선택 안 함 — 리랭크 skip)</option>
                    {(endpointsQuery.data?.endpoints ?? [])
                      .filter((e) => !e.supportsEmbedding)
                      .map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.displayName ?? e.name} ({e.model})
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium" htmlFor="cfg-reranker-topk">
                    리랭크 상위 K
                  </label>
                  <p className="mb-2 text-xs text-foreground-muted">
                    RRF 상위 몇 개만 리랭크할지. 작을수록 빠르고 비용↓ (기본 8).
                  </p>
                  <input
                    id="cfg-reranker-topk"
                    type="number"
                    min={1}
                    max={50}
                    value={rerankerTopK}
                    onChange={(e) => setRerankerTopK(Number(e.currentTarget.value))}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
                  />
                </div>
              </>
            ) : null}
          </section>

          <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cfg-concurrency">
                동시 처리 갯수
              </label>
              <input
                id="cfg-concurrency"
                type="number"
                min={1}
                max={20}
                value={concurrency}
                onChange={(e) => setConcurrency(Number(e.currentTarget.value))}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <p className="mt-1 text-xs text-foreground-faint">
                wiki.ingest 워커 in-process p-limit. 변경 시 서버 재시작 필요.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cfg-charcap">
                자료 글자 상한
              </label>
              <input
                id="cfg-charcap"
                type="number"
                min={1000}
                max={500000}
                step={1000}
                value={charCap}
                onChange={(e) => setCharCap(Number(e.currentTarget.value))}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <p className="mt-1 text-xs text-foreground-faint">
                한 번 LLM 호출에 전달할 parsed_markdown 최대 글자수.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cfg-iterlimit">
                tool 호출 iteration 상한
              </label>
              <input
                id="cfg-iterlimit"
                type="number"
                min={3}
                max={50}
                value={iterLimit}
                onChange={(e) => setIterLimit(Number(e.currentTarget.value))}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <p className="mt-1 text-xs text-foreground-faint">
                ingest LLM 의 read/write tool 반복 횟수 한도.
              </p>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cfg-max-chunks">
                자료당 최대 청크 수
              </label>
              <input
                id="cfg-max-chunks"
                type="number"
                min={1}
                max={500}
                value={maxChunks}
                onChange={(e) => setMaxChunks(Number(e.currentTarget.value))}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <p className="mt-1 text-xs text-foreground-faint">
                한 자료에서 처리할 최대 청크 수 (비용 cap). 큰 PDF (3000+ 페이지) 는 50 으로 잘리니
                비싼 endpoint 면 작게, 무료 endpoint 면 200~500.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cfg-chunk-max-chars">
                청크 최대 글자수
              </label>
              <input
                id="cfg-chunk-max-chars"
                type="number"
                min={1000}
                max={20000}
                step={500}
                value={chunkMaxChars}
                onChange={(e) => setChunkMaxChars(Number(e.currentTarget.value))}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <p className="mt-1 text-xs text-foreground-faint">
                청크 1개당 최대 글자수 (~2-3k 토큰 권장). 표/코드 많으면 작게, 서술 자료는
                8000~10000.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cfg-chunk-overlap">
                청크 간 겹침 글자수
              </label>
              <input
                id="cfg-chunk-overlap"
                type="number"
                min={0}
                max={2000}
                step={50}
                value={chunkOverlap}
                onChange={(e) => setChunkOverlap(Number(e.currentTarget.value))}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <p className="mt-1 text-xs text-foreground-faint">
                청크 경계 컨텍스트 손실 방지용. heading boundary 가 잘 잡히면 0 OK, plain 텍스트는
                200~500.
              </p>
            </div>
          </section>

          <p className="text-xs text-foreground-faint">
            ↑ 청크/endpoint 설정 변경은 다음 ingest 부터 적용됩니다 (진행 중인 작업에는 영향 없음).
          </p>

          <div className="flex items-center gap-3 border-t border-border pt-6">
            <button
              type="button"
              onClick={() =>
                updateMutation.mutate({
                  schemaPrompt: draft.schemaPrompt ?? '',
                  modeIngestPrompt: draft.modeIngestPrompt ?? '',
                  modeHealthPrompt: draft.modeHealthPrompt ?? '',
                  chatInjectPrompt: draft.chatInjectPrompt ?? '',
                  ingestConcurrency: concurrency,
                  ingestMarkdownCharCap: charCap,
                  ingestToolIterationLimit: iterLimit,
                  ingestMaxChunks: maxChunks,
                  ingestChunkMaxChars: chunkMaxChars,
                  ingestChunkOverlap: chunkOverlap,
                  visionLlmEndpointId: visionEndpointId || null,
                  ingestLlmEndpointId: ingestEndpointId || null,
                  healthLlmEndpointId: healthEndpointId || null,
                  embeddingLlmEndpointId: embeddingEndpointId || null,
                  invalidateMismatchedEmbeddings: invalidateMismatched,
                  rerankerKind: 'llm',
                  rerankerLlmEndpointId: rerankerEnabled ? rerankerEndpointId || null : null,
                  rerankerTopK,
                })
              }
              disabled={updateMutation.isPending}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:opacity-90 disabled:opacity-50"
            >
              {updateMutation.isPending ? '저장 중…' : '저장'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    '4개 프롬프트를 모두 기본값으로 복원합니다. 현재 작성된 내용은 사라집니다. 계속하시겠습니까?',
                  )
                ) {
                  resetMutation.mutate();
                }
              }}
              disabled={resetMutation.isPending}
              className="rounded-md border border-border px-4 py-2 text-sm hover:border-accent disabled:opacity-50"
            >
              기본값으로 복원
            </button>
            {savedMsg ? <span className="text-sm text-emerald-600">{savedMsg}</span> : null}
          </div>
        </div>
      )}
    </PageShell>
  );
}
